/*
 * Jira Ticket:
 * Created Date: Tue, 24th Mar 2020, 17:42:42 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState } from 'react';
import {
  Confirm,
  DELETE,
  Edit,
  fetchEnd,
  fetchStart,
  ReferenceArrayInput,
  required,
  SaveButton,
  SelectArrayInput,
  SelectInput,
  SimpleForm,
  TextInput,
  useDataProvider,
  useNotify,
  useRedirect,
  useTranslate,
  usePermissions,
} from 'react-admin';
import { connect } from 'react-redux';
import { AccessDenied } from 'Layout';
import { Delete } from '@material-ui/icons';
import { TemplateToolbar } from 'Components';
import { ActionButton as RevokeButton } from 'Components/Buttons';
import {
  dynamicInviteRoleOptions,
  employmentTypeOptions,
  inviteRoleOptions,
} from 'utils/choices';
import { getMaximumRole, isProjectManager, isSuperAdmin } from 'utils/roles';
import { toolbarStyles } from 'styles';

const RevokeToolbar = ({ classes, setShowConfirmModal, ...props }) => {
  return (
    <TemplateToolbar
      children={
        <div className={classes.root}>
          <SaveButton {...props} />
          <RevokeButton
            name={'actions.removeUser'}
            onClick={() => setShowConfirmModal(true)}
            className={classes.deleteButton}
            variant="text"
            icon={<Delete />}
          />
        </div>
      }
      {...props}
    />
  );
};

const EmploymentEdit = ({
  hasCreate,
  hasEdit,
  hasList,
  hasShow,
  dispatch,
  formValues,
  ...props
}) => {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const employmentId = props.id;
  const { organisation, user, ...formData } =
    (formValues && formValues[employmentId]) || {};
  const classes = toolbarStyles();
  const { prevLocation } = props.location.state || {};

  const onClickRevoke = async isSuperAdmin => {
    const userPrevRegex = new RegExp(/^\/User\/[a-zA-Z0-9-]+\/1$/);
    setIsSubmitting(true);
    dispatch(fetchStart());
    return dataProvider(DELETE, 'Employment', {
      id: employmentId,
    })
      .then(result => {
        dispatch(fetchEnd());
        closeModal();
        if (isSuperAdmin) {
          return prevLocation
            ? redirect(prevLocation) // if we have a previous location, go there
            : user.id
            ? redirect(`/User/${user.id}/1`)
            : redirect(`/User`);
        }
        // not a super admin; instead redirect back to the user list as you
        // might no longer be able to view the user removed from your org
        if (prevLocation && userPrevRegex.test(prevLocation))
          return redirect(`/User`);
        return redirect(prevLocation); // not a super admin; redirect back to the org users tab
      })
      .catch(error => {
        dispatch(fetchEnd());
        const {
          extensions: { code },
          message,
        } = error.graphQLErrors[0];
        if (code !== 'INTERNAL_SERVER_ERROR') {
          return notify(message, 'warning');
        }
        return notify('error.generic', 'warning');
      });
  };

  const closeModal = () => {
    setIsSubmitting(false);
    return setShowConfirmModal(false);
  };

  const currentRole = getMaximumRole(
    permissions,
    organisation && organisation.id
  );

  return organisation && isProjectManager(permissions, organisation.id) ? (
    <Edit title={'resources.Employment.fields.title'} {...props}>
      <SimpleForm
        redirect={prevLocation || false}
        toolbar={
          <RevokeToolbar
            classes={classes}
            setShowConfirmModal={setShowConfirmModal}
            {...props}
          />
        }
      >
        <TextInput
          source="organisation.name"
          label={'resources.Employment.fields.organisation'}
          disabled={true}
        />
        <SelectInput
          source="type"
          label={'resources.Employment.fields.accessTo'}
          disabled={true}
          choices={employmentTypeOptions}
        />
        {currentRole ? ( //have to check permissions is not undefined first or hit warnings in field
          <SelectInput
            source="role"
            label={'resources.Employment.fields.role'}
            choices={
              formData.type !== 'FREELANCER'
                ? dynamicInviteRoleOptions(currentRole)
                : inviteRoleOptions(formData.maxRole)
            }
            validate={required()}
          />
        ) : null}
        {formData.type === 'FREELANCER' ? (
          <ReferenceArrayInput
            label={'resources.Employment.fields.projects'}
            source="projectIds"
            resource="Project"
            reference="Project"
            filter={{
              principalOrganisationId: organisation && organisation.id,
            }} // get the organisation's projects including subcontracted projects
            perPage={1000}
            fullWidth
            validate={required()}
            sort={{ field: 'name', order: 'ASC' }}
          >
            <SelectArrayInput optionText="name" />
          </ReferenceArrayInput>
        ) : null}
        <Confirm
          isOpen={showConfirmModal}
          loading={isSubmitting}
          title={translate(
            'resources.Employment.fields.deleteEmploymentModalTitle'
          )}
          content={translate(
            'resources.Employment.fields.deleteEmploymentModalContent',
            {
              orgName: organisation && organisation.name,
              email: (user && user.email) || 'no email found',
            }
          )}
          onConfirm={() => onClickRevoke(isSuperAdmin(permissions))}
          onClose={() => closeModal()}
        />
      </SimpleForm>
    </Edit>
  ) : (
    <AccessDenied />
  );
};

const mapStateToProps = state => ({
  formValues:
    state.admin.resources.Employment && state.admin.resources.Employment.data,
});

export default connect(mapStateToProps)(EmploymentEdit);
